import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useCrmAudit } from "@/hooks/use-crm-audit";
import type { DBSignalQueueItem } from "@/lib/db-types";

type QueueSnapshot = [readonly unknown[], DBSignalQueueItem[] | undefined][];

/** Fjerner kortet fra køen med en gang, så neste vises uten blink. Returnerer forrige tilstand for rollback. */
async function removeFromQueue(qc: QueryClient, signalId: string): Promise<QueueSnapshot> {
  await qc.cancelQueries({ queryKey: ["signal-queue"] });
  const previous = qc.getQueriesData<DBSignalQueueItem[]>({ queryKey: ["signal-queue"] });
  qc.setQueriesData<DBSignalQueueItem[]>({ queryKey: ["signal-queue"] }, (old) => old?.filter((s) => s.id !== signalId));
  return previous;
}

function restoreQueue(qc: QueryClient, previous: QueueSnapshot | undefined) {
  previous?.forEach(([key, data]) => qc.setQueryData(key, data));
}

export const SIGNAL_ALREADY_CLAIMED_MESSAGE = "Noen andre tok dette akkurat nå";

/** claim_signal kaster signal_already_claimed når en annen rakk det først. */
export function isSignalAlreadyClaimed(err: unknown): boolean {
  const message = err instanceof Error ? err.message : typeof err === "object" && err && "message" in err ? String((err as { message: unknown }).message) : "";
  return message.includes("signal_already_claimed");
}

export interface ClaimSignalVars {
  signalId: string;
  customerId?: string;
  /** new: lag en ny prosess. attach: legg signalet til en eksisterende prosess. */
  mode: "new" | "attach";
  opportunityId?: string | null;
  title?: string | null;
  theme?: string | null;
}

export interface ClaimSignalResult {
  opportunity_id: string | null;
  follow_up_id: string | null;
}

/**
 * Ta ansvar for et signal via RPC claim_signal (atomisk: første som tar får det).
 * RPC-en bruker den innloggede brukeren, også ved impersonering.
 */
export function useClaimSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: ClaimSignalVars): Promise<ClaimSignalResult> => {
      if (vars.mode === "attach" && !vars.opportunityId) throw new Error("Velg hvilken prosess signalet skal legges til");
      const { data, error } = await supabase.rpc("claim_signal", {
        p_signal_id: vars.signalId,
        p_mode: vars.mode,
        p_opportunity_id: vars.opportunityId ?? null,
        p_title: vars.title ?? null,
        p_theme: vars.theme ?? null,
      });
      if (error) throw error;
      return data as ClaimSignalResult;
    },
    onMutate: (vars) => removeFromQueue(qc, vars.signalId),
    onSuccess: async (_result, vars) => {
      await invalidateFor(qc, "customer_signals", { customerId: vars.customerId });
      // Prosessen og oppfølgingen er nye for køen og pipen.
      await invalidateFor(qc, "opportunities", { customerId: vars.customerId });
      await invalidateFor(qc, "follow_ups", { customerId: vars.customerId });
    },
    onError: async (err: Error, vars, previous) => {
      // Tapt kappløp er normalt: kortet er borte for godt, og køen viser neste.
      if (isSignalAlreadyClaimed(err)) {
        toast.info(SIGNAL_ALREADY_CLAIMED_MESSAGE);
        await invalidateFor(qc, "customer_signals", { customerId: vars.customerId });
      } else {
        restoreQueue(qc, previous);
        toast.error(`Kunne ikke ta ansvar: ${err.message}`);
      }
    },
  });
}

/** «Ikke for meg»: skjuler signalet for den innloggede brukeren. Audit skrives av DB-triggeren. */
export function useDismissSignal() {
  const qc = useQueryClient();
  const { authUserId } = useCrmAudit();
  return useMutation({
    mutationFn: async (vars: { signalId: string; customerId?: string }) => {
      if (!authUserId) throw new Error("Ikke innlogget");
      const { error } = await supabase
        .from("signal_dismissals")
        .insert({ signal_id: vars.signalId, user_id: authUserId });
      // 23505: allerede skjult (to hurtigtrykk), samme utfall.
      if (error && (error as { code?: string }).code !== "23505") throw error;
    },
    onMutate: (vars) => removeFromQueue(qc, vars.signalId),
    onSuccess: async (_void, vars) => {
      await invalidateFor(qc, "signal_dismissals", { customerId: vars.customerId });
    },
    onError: (err: Error, _vars, previous) => {
      restoreQueue(qc, previous);
      toast.error(`Kunne ikke skjule signalet: ${err.message}`);
    },
  });
}

/** Admin: arkiver et åpent signal for alle (RPC archive_signal_for_all). */
export function useArchiveSignalForAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { signalId: string; customerId?: string }) => {
      const { error } = await supabase.rpc("archive_signal_for_all", { p_signal_id: vars.signalId });
      if (error) throw error;
    },
    onMutate: (vars) => removeFromQueue(qc, vars.signalId),
    onSuccess: async (_void, vars) => {
      await invalidateFor(qc, "customer_signals", { customerId: vars.customerId });
    },
    onError: (err: Error, _vars, previous) => {
      restoreQueue(qc, previous);
      toast.error(`Kunne ikke arkivere: ${err.message}`);
    },
  });
}
